import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, RefreshCw, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b7533cee-6cc1-4a41-9e88-4b20184fec89/artifacts/eixt3fs2_STM%20mobile%20logo.png';

const CheckEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUser, isAuthenticated } = useAuth();
  
  // Get email from location state (passed from signup) or from authenticated user
  const email = location.state?.email || user?.email || '';
  
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // If no email and not authenticated, redirect to signup
  useEffect(() => {
    if (!email && !isAuthenticated) {
      navigate('/signup');
    }
  }, [email, isAuthenticated, navigate]);

  useEffect(() => {
    // Countdown timer for resend cooldown
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResend = async () => {
    if (resendCooldown > 0 || !email) return;
    
    setResending(true);
    try {
      await axios.post(`${API_BASE}/api/auth/resend-verification`, { email });
      toast.success('Verification email sent! Please check your inbox.');
      setResendCooldown(60);
    } catch (error) {
      const errorData = error.response?.data?.detail;
      if (errorData?.code === 'RATE_LIMITED') {
        setResendCooldown(errorData.retry_after || 60);
        toast.error(errorData.error);
      } else if (errorData?.code === 'DAILY_LIMIT_REACHED') {
        toast.error(errorData.error);
      } else {
        toast.error('Failed to resend verification email.');
      }
    } finally {
      setResending(false);
    }
  };

  const goToLogin = () => {
    navigate('/login');
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{
        background: 'linear-gradient(135deg, #0B0F1A 0%, #0E1022 50%, #12162B 100%)'
      }}
    >
      {/* Subtle background glow */}
      <div 
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(124, 92, 255, 0.08) 0%, transparent 60%)'
        }}
      />

      <div className="w-full max-w-md relative z-10">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-4">
            <img 
              src={LOGO_URL} 
              alt="ScopeTheMarket" 
              className="h-10 w-auto"
              style={{ filter: 'drop-shadow(0 0 10px rgba(59, 130, 246, 0.5))' }}
            />
            <span className="text-xl font-semibold" style={{ color: '#F4F4F5' }}>
              ScopeTheMarket
            </span>
          </Link>
        </div>

        {/* Check Email Card */}
        <div 
          className="rounded-2xl p-8"
          style={{
            backgroundColor: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <div className="text-center">
            {/* Email Icon */}
            <div 
              className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
            >
              <Mail className="h-10 w-10" style={{ color: '#3B82F6' }} />
            </div>

            <h2 className="text-2xl font-bold mb-2" style={{ color: '#F4F4F5' }}>
              Verify your email
            </h2>
            
            <p className="mb-2" style={{ color: '#94A3B8' }}>
              We've sent a verification link to:
            </p>
            
            {email && (
              <p className="font-medium mb-4" style={{ color: '#F4F4F5' }}>
                {email}
              </p>
            )}

            {/* Important notice */}
            <div 
              className="flex items-start gap-3 p-4 rounded-lg mb-6 text-left"
              style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)' }}
            >
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#FBBF24' }} />
              <div>
                <p className="text-sm font-medium" style={{ color: '#FBBF24' }}>
                  Verification required
                </p>
                <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                  You must verify your email within 24 hours to activate your account.
                  Unverified accounts are automatically deleted.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={handleResend}
                disabled={resending || resendCooldown > 0}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all disabled:opacity-50"
                style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#F4F4F5' }}
              >
                {resending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {resendCooldown > 0 
                  ? `Resend in ${resendCooldown}s` 
                  : 'Resend verification email'}
              </button>

              <button
                onClick={goToLogin}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all"
                style={{ backgroundColor: '#3B82F6', color: 'white' }}
              >
                <CheckCircle className="h-4 w-4" />
                I've verified - Go to login
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
              <span className="text-xs" style={{ color: '#64748B' }}>or</span>
              <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
            </div>

            {/* Use different email */}
            <Link 
              to="/signup"
              className="text-sm underline"
              style={{ color: '#3B82F6' }}
            >
              Use a different email address
            </Link>
          </div>
        </div>

        {/* Tips */}
        <div 
          className="mt-6 p-4 rounded-xl text-sm"
          style={{ backgroundColor: 'rgba(30, 41, 59, 0.3)' }}
        >
          <p className="font-medium mb-2" style={{ color: '#F4F4F5' }}>
            Didn't receive the email?
          </p>
          <ul className="space-y-1" style={{ color: '#94A3B8' }}>
            <li>• Check your spam or junk folder</li>
            <li>• Make sure {email || 'your email'} is correct</li>
            <li>• Wait a few minutes and try again</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CheckEmail;
